'use client';

import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';

export default function ProjectsSection() {
  const { t } = useLanguage();
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const filteredProjects = activeCategory === 'all'
    ? t.projects.items
    : t.projects.items.filter((item) => item.sectorId === activeCategory);

  return (
    <section
      id="projectes"
      className="py-24 px-6 bg-[#050507] relative overflow-hidden border-t border-white/5"
      aria-label="Projectes i Demos per sectors"
    >
      <div className="max-w-7xl mx-auto space-y-12 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-[11px] font-bold uppercase tracking-widest">
            {t.projects.badge}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase">
            {t.projects.title}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            {t.projects.sub}
          </p>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {t.projects.categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 sm:px-5 py-2.5 rounded-full text-xs font-bold transition-all border ${
                activeCategory === cat.id
                  ? 'bg-gold text-black border-gold shadow-lg shadow-gold/20 font-black'
                  : 'bg-white/5 text-slate-300 border-white/10 hover:border-gold/40'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {filteredProjects.map((project, idx) => (
            <div
              key={idx}
              className="rounded-3xl p-7 sm:p-8 bg-gradient-to-b from-[#11111a] to-[#0a0a0f] border border-white/10 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/5 transition-all duration-300 flex flex-col justify-between group space-y-6"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-widest text-gold bg-gold/10 px-3 py-1 rounded-full border border-gold/20">
                    {project.category}
                  </span>

                </div>

                <h3 className="text-2xl font-black text-white group-hover:text-gold transition-colors">
                  {project.title}
                </h3>

                <p className="text-sm text-slate-300 leading-relaxed">
                  {project.description}
                </p>

                {/* Highlight badge */}
                <div className="p-3.5 rounded-xl bg-purple-brand/15 border border-purple-brand/30 text-xs text-purple-200">
                  <strong className="text-magenta-brand block text-[10px] font-extrabold uppercase tracking-wider">
                    {t.projects.keyHighlight}
                  </strong>
                  {project.highlight}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {project.tags.map((tag, tIdx) => (
                    <span
                      key={tIdx}
                      className="text-[10px] font-medium text-slate-300 bg-white/5 px-2.5 py-1 rounded-md border border-white/5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action row */}
              <div className="pt-5 border-t border-white/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-black font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 transition-all hover:scale-[1.02]"
                >
                  <span>🚀 {t.projects.openDemoButton}</span>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <a
                  href={`https://wa.me/34694919757?text=Hola%2C%20vull%20una%20web%20com%20la%20demo%20de%20${encodeURIComponent(project.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold text-slate-400 hover:text-white flex items-center justify-center gap-1.5 transition-colors py-2 sm:py-0"
                >
                  <span>💬 {t.projects.askCustomButton}</span>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
